import { getApiBaseUrl, getApiOrigin } from "./apiConfig";

const GIRAFDEV_IMAGE_BASE = "https://salesammo-api.girafdev.com/api";
const RENDER_IMAGE_BASE = "https://salesammo-backend.onrender.com/api";
const LOCAL_IMAGE_BASE = "http://localhost:5000/api";

const getPageHost = () =>
  typeof window !== "undefined" ? window.location.hostname : "";

const isLocalPage = () => /^(localhost|127\.0\.0\.1)$/i.test(getPageHost());
const isGirafdevPage = () => /girafdev\.com$/i.test(getPageHost());

export const getImageBaseUrl = () => {
  if (isGirafdevPage()) return GIRAFDEV_IMAGE_BASE;
  if (isLocalPage()) {
    const fromEnv = process.env.REACT_APP_IMAGE_BASE_URL?.trim();
    if (fromEnv) return fromEnv.replace(/\/+$/, "");
    return LOCAL_IMAGE_BASE;
  }

  const fromEnv = process.env.REACT_APP_IMAGE_BASE_URL?.trim();
  if (fromEnv) return fromEnv.replace(/\/+$/, "");

  const origin = getApiOrigin(getApiBaseUrl());
  if (origin) return `${origin}/api`;

  return GIRAFDEV_IMAGE_BASE;
};

const toPathString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return (
    value.url ||
    value.path ||
    value.image ||
    value.uri ||
    value.thumbnail_image ||
    value.profilePicture ||
    value.logo ||
    value.filename ||
    ""
  );
};

const encodePathSegments = (relativePath) =>
  String(relativePath)
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      try {
        return encodeURIComponent(decodeURIComponent(segment));
      } catch {
        return encodeURIComponent(segment);
      }
    })
    .join("/");

const unique = (items) => [...new Set(items.filter(Boolean))];

const isHttpsPage = () =>
  typeof window !== "undefined" && window.location.protocol === "https:";

const canUseUrl = (url) => {
  if (!url) return false;
  if (isHttpsPage() && /^http:\/\//i.test(url)) return false;
  return true;
};

const getUploadsRelativePath = (raw) => {
  const path = String(raw || "")
    .replace(/\\/g, "/")
    .trim();
  if (!path) return "";

  const fromAbsolute = path.match(/\/(?:api\/)?(uploads\/.+)$/i);
  if (fromAbsolute?.[1]) return fromAbsolute[1];

  let relative = path.replace(/^https?:\/\/[^/]+/i, "");
  relative = relative.replace(/^\/+/, "").replace(/^api\//i, "");
  const uploadsMatch = relative.match(/(uploads\/.+)$/i);
  if (uploadsMatch?.[1]) return uploadsMatch[1];
  if (/^uploads\//i.test(relative)) return relative;
  return relative;
};

const withAndWithoutApi = (base) => {
  const cleanBase = String(base || "").replace(/\/+$/, "");
  if (!cleanBase) return [];
  if (cleanBase.endsWith("/api")) {
    return [cleanBase, cleanBase.replace(/\/api$/i, "")];
  }
  return [cleanBase, `${cleanBase}/api`];
};

const getImageBases = () => {
  const extra = String(process.env.REACT_APP_IMAGE_FALLBACK_BASES || "")
    .split(",")
    .map((item) => item.trim().replace(/\/+$/, ""))
    .filter(Boolean);

  const apiOrigin = getApiOrigin(getApiBaseUrl());

  return unique([
    getImageBaseUrl(),
    isGirafdevPage() ? GIRAFDEV_IMAGE_BASE : "",
    isLocalPage() ? LOCAL_IMAGE_BASE : "",
    apiOrigin ? `${apiOrigin}/api` : "",
    GIRAFDEV_IMAGE_BASE,
    RENDER_IMAGE_BASE,
    ...extra,
  ]).filter(canUseUrl);
};

export const getImageUrlCandidates = (value) => {
  const raw = toPathString(value).trim();
  if (!raw) return [];
  if (raw.startsWith("blob:") || raw.startsWith("data:")) return [raw];
  if (/^(file:|content:|ph:|assets-library:)/i.test(raw)) return [];

  const relative = getUploadsRelativePath(raw);
  const encoded = encodePathSegments(relative);
  const urls = [];

  if (/^https?:\/\//i.test(raw) && canUseUrl(raw)) {
    urls.push(raw);
  }

  getImageBases().forEach((base) => {
    if (!encoded) return;
    withAndWithoutApi(base).forEach((cleanBase) => {
      urls.push(`${cleanBase}/${encoded}`);
    });
  });

  // CRA proxy only exists on localhost. On girafdev these hit the admin host and 404.
  if (isLocalPage() && encoded.startsWith("uploads/")) {
    urls.push(`/api/${encoded}`, `/${encoded}`);
  }

  return unique(urls).filter(canUseUrl);
};

export const resolveImageUrl = (value) => getImageUrlCandidates(value)[0] || "";
